import {readNativeProductionReferenceInventory,nativeProductionReferencesAreCurrent,type NativeProductionReferenceDescriptor} from './source-scene-reference-inventory';
/** Pure native graph contract. No React, provider, service, or client URL execution. */
import { sha256HexOfBytes } from '../../../src/lib/bytes-hash';
import { preserveSceneGenSpec } from '../../../src/lib/agent/scene-gen-spec-semantics';
import { copyEditorJson, editorValueSignature, type NativeWorkScope } from './native-envelope';
import type { SceneGenSpec } from '../../../src/lib/agent/scene-gen-spec-types';
import type { SceneSpecRegenFields, SceneSpecAnchorMode } from '../../../src/lib/agent/scene-spec-regen-core';
import type { SceneSpecDurationInput, SceneSpecDurationBounds } from '../../../src/lib/agent/scene-spec-duration';
import type { VideoRetouchInstruction, VideoRetouchNode, VideoRetouchEligibility, VideoRetouchEstimate } from '../../../src/lib/omni-video-retouch-core';
export type { SceneGenSpec };
export type NativeSceneSpec = {
    version: 1;
    sceneId: string;
    ownerItemId: string;
    /** Explicit native setting; false excludes legacy production reference media only. */
    sourceReferencesEnabled?: boolean;
    sourceReferences?:NativeProductionReferenceDescriptor[];
    capsule?: SceneGenSpec;
    regenFields?: SceneSpecRegenFields;
    durationContext?: SceneSpecDurationInput;
};
export type NativeSceneGenerationContext = {
    version: 1;
    cast: Array<{
        name: string;
        mainAssetId: string;
    }>;
};
/** Required in shared asset metadata; request remains unchanged during uncertain completion/retry. */
export type NativeSceneOutputReceipt = {
    version: 1;
    attachmentId: string;
    request: SourceSceneGenerationRequest;
    appliedTo?: SceneGenerationTarget;
    originalItem?: Record<string, unknown>;
    originalOwner?: Record<string, unknown>;
};
/** Local settings binding; media readiness is required only by generation targets. */
export type SceneSpecTarget = {
    scope: NativeWorkScope;
    revision: string;
    itemId: string;
    itemLineage: string;
    sceneId: string;
    ownerItemId: string;
    documentSignature: string;
    itemSignature: string;
    sceneSignature: string;
};
/** Execution binding remains strict and carries the actual source-window signature. */
export type SceneGenerationTarget = SceneSpecTarget & { sourceSignature: string };
export type SourceWindow = {
    assetId: string;
    remoteUrl: string;
    assetSignature: string;
    from: number;
    end: number;
    fps: number;
    sourceStartSeconds: number;
    sourceEndSeconds: number;
    playbackRate: number;
    originalDurationSeconds: number;
    type: 'video' | 'image';
};
export type ResolvedSceneReference = {
    role: 'start' | 'scene' | 'reference' | 'consistency';
    assetId: string;
    remoteUrl: string;
    signature: string;
    label?: string;
    castName?: string;
};
export type SourceSceneGenerationInput = {
    version: 1;
    requestId: string;
    mode: 'regenerate' | 'retouch';
    target: SceneGenerationTarget;
    durationPolicy: 'preserve-slot';
    instruction?: VideoRetouchInstruction;
};
export type SourceSceneGenerationRequest = SourceSceneGenerationInput & {
    source: SourceWindow;
    capsule?: SceneGenSpec;
    capsuleFingerprint: string;
    fields: SceneSpecRegenFields;
    anchorMode: SceneSpecAnchorMode;
    references: ResolvedSceneReference[];
    previousEnd?: SourceWindow;
    outputDurationSeconds: number;
    retouchPrompt?: string;
    estimate?: VideoRetouchEstimate;
    requestSignature: string;
};
/** Inject existing canonical helpers at host boundary. Never reimplement provider rules here. */
export type SceneGenerationSemantics = {
    preserveSceneGenSpec(raw: unknown): {
        spec?: SceneGenSpec;
        droppedKeys: string[];
    };
    sceneGenSpecFingerprint(spec: SceneGenSpec | undefined): string;
    sceneSpecDurationBounds(input: SceneSpecDurationInput): SceneSpecDurationBounds;
    sceneSpecDurationBlocked(value: number | undefined, bounds: SceneSpecDurationBounds): string | null;
    applySceneSpecToRegenFields(fields: SceneSpecRegenFields, spec: SceneGenSpec | undefined): SceneSpecRegenFields;
    sceneSpecAnchorMode(spec: SceneGenSpec | undefined): SceneSpecAnchorMode;
    videoRetouchEligibility(node: VideoRetouchNode, opts: {
        freeMode: boolean;
    }): VideoRetouchEligibility;
    omniRetouchEstimate(seconds: number): VideoRetouchEstimate;
    omniRetouchPromptOf(instruction: VideoRetouchInstruction): string | null;
    videoRetouchFreeTextIssue(text: string, target?: string): string | null;
};
export type SceneGraph = {
    items: Record<string, Record<string, unknown>>;
    assets: Record<string, Record<string, unknown>>;
    tracks: Array<Record<string, unknown> & {
        id: string;
        items: string[];
        locked: boolean;
    }>;
    fps: number;
    compositionWidth: number;
    compositionHeight: number;
    sourceSceneGeneration?: NativeSceneGenerationContext;
};
export async function sourceSceneGenerationSignature(value: unknown): Promise<string> { return sha256HexOfBytes(new TextEncoder().encode(editorValueSignature(value)).buffer as ArrayBuffer); }
export const sceneRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
export const sceneId = (v: unknown): v is string => typeof v === 'string' && v.length > 0 && v.length <= 512 && !['__proto__', 'constructor', 'prototype'].includes(v);
const positive = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;
const frame = (v: unknown): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0 && v <= 10000000;
const fail = (message = '씬이나 원본이 바뀌었습니다. 다시 선택해 주세요.'): never => { throw Error(message); };
export function readSceneGraph(raw: unknown): SceneGraph {
    if (!sceneRecord(raw) || !sceneRecord(raw.items) || !sceneRecord(raw.assets) || !Array.isArray(raw.tracks) || !positive(raw.fps) || !positive(raw.compositionWidth) || !positive(raw.compositionHeight))
        return fail('편집 자료를 확인해 주세요.');
    if (!Object.entries(raw.items).every(([id, i]) => sceneId(id) && sceneRecord(i) && i.id === id) || !Object.entries(raw.assets).every(([id, a]) => sceneId(id) && sceneRecord(a) && a.id === id) || !raw.tracks.every(t => sceneRecord(t) && sceneId(t.id) && Array.isArray(t.items) && t.items.every(sceneId) && typeof t.locked === 'boolean'))
        return fail();
    if (new Set(raw.tracks.map(t => (t as Record<string, unknown>).id)).size !== raw.tracks.length)
        return fail();
    return raw as unknown as SceneGraph;
}
export function sceneScopeMatches(a: NativeWorkScope, b: NativeWorkScope): boolean { return a.projectId === b.projectId && a.workId === b.workId && a.surface === b.surface; }
export function readSceneScope(raw: unknown): NativeWorkScope {
    if (!sceneRecord(raw) || !sceneId(raw.projectId) || !sceneId(raw.workId) || !['agent', 'editor'].includes(String(raw.surface)))
        return fail('작업 범위를 확인해 주세요.');
    return { projectId: raw.projectId, workId: raw.workId, surface: raw.surface as NativeWorkScope['surface'] };
}
/** Members use absolute frames; ancestors clip visible windows without adding origins. */
export function sceneItemOwnership(document: SceneGraph, id: string): {
    track: SceneGraph['tracks'][number];
    ancestors: Record<string, unknown>[];
    from: number;
    end: number;
} {
    let current = document.items[id];
    if (!current || !frame(current.from) || !positive(current.durationInFrames) || !Number.isSafeInteger(current.durationInFrames))
        return fail();
    let from = current.from, end = from + current.durationInFrames;
    const ancestors: Record<string, unknown>[] = [], seen = new Set<string>();
    for (;;) {
        const currentId = String(current.id);
        if (seen.has(currentId))
            return fail();
        seen.add(currentId);
        const tracks = document.tracks.filter(t => t.items.includes(currentId));
        const parents = Object.values(document.items).filter(i => i.type === 'group' && Array.isArray(i.children) && i.children.includes(currentId));
        if (tracks.length + parents.length !== 1 || tracks.some(t => t.items.filter(x => x === currentId).length !== 1) || parents.some(p => (p.children as unknown[]).filter(x => x === currentId).length !== 1))
            return fail();
        if (tracks.length) {
            const track = tracks[0]!;
            if (track.locked || end <= from)
                return fail('잠금을 해제하거나 보이는 컷을 다시 선택해 주세요.');
            return { track, ancestors, from, end };
        }
        current = parents[0]!;
        ancestors.push(current);
        if (!frame(current.from) || !positive(current.durationInFrames) || !Number.isSafeInteger(current.durationInFrames))
            return fail();
        if (current.preserveGroupFrame === true) {
            from = Math.max(from, current.from);
            end = Math.min(end, current.from + current.durationInFrames);
        }
    }
}
function trustedAsset(document: SceneGraph, id: string, type?: 'image' | 'video'): {
    asset: Record<string, unknown>;
    url: string;
} {
    const asset = document.assets[id];
    if (!asset || !['image', 'video'].includes(String(asset.type)) || (type && asset.type !== type) || typeof asset.remoteUrl !== 'string')
        return fail('현재 작업의 업로드된 원본을 선택해 주세요.');
    const url = new URL(asset.remoteUrl);
    if (url.protocol !== 'https:' || url.username || url.password)
        return fail('원본을 자료함에 업로드한 뒤 선택해 주세요.');
    return { asset, url: url.href };
}
export async function resolveSceneSourceWindow(document: SceneGraph, itemId: string): Promise<SourceWindow> {
    const item = document.items[itemId];
    if (!item || !['video', 'image'].includes(String(item.type)) || !sceneId(item.assetId))
        return fail('영상 또는 이미지 컷 하나를 선택해 주세요.');
    const type = item.type as 'video' | 'image', owned = sceneItemOwnership(document, itemId), { asset, url } = trustedAsset(document, item.assetId, type);
    const rate = type === 'video' ? item.playbackRate : 1, start = type === 'video' ? item.videoStartFromInSeconds : 0;
    if (!positive(rate) || typeof start !== 'number' || !Number.isFinite(start) || start < 0)
        return fail();
    const sourceStartSeconds = start + (owned.from - Number(item.from)) / document.fps * rate;
    let sourceEndSeconds = sourceStartSeconds + (owned.end - owned.from) / document.fps * rate;
    const duration = type === 'video' ? asset.durationInSeconds : sourceEndSeconds;
    if(positive(duration)&&sourceEndSeconds>duration&&sourceEndSeconds-duration<rate/document.fps-1e-7)sourceEndSeconds=duration;
    if (!positive(duration) || sourceStartSeconds >= duration || sourceEndSeconds > duration + 1e-7)
        return fail('컷이 원본 재생 구간을 벗어났습니다. 조정한 뒤 다시 선택해 주세요.');
    return { assetId: item.assetId, remoteUrl: url, assetSignature: await sourceSceneGenerationSignature(asset), from: owned.from, end: owned.end, fps: document.fps, sourceStartSeconds, sourceEndSeconds, playbackRate: rate, originalDurationSeconds: duration, type };
}
/** Native floor/round/ceil may represent a whole file within one source-time frame. */
export function isWholeNativeVideoSource(source:SourceWindow):boolean{
 const frameSeconds=source.playbackRate/source.fps,physical=source.originalDurationSeconds;
 return source.type==='video'&&source.sourceStartSeconds===0&&Math.abs(source.sourceEndSeconds-physical)<frameSeconds-1e-7
  &&Math.abs((source.end-source.from)/source.fps*source.playbackRate-physical)<frameSeconds-1e-7;
}
export function readNativeSceneSpec(raw: unknown, ownerItemId: string, sceneIdValue: string, helpers: Pick<SceneGenerationSemantics, 'preserveSceneGenSpec'>): NativeSceneSpec {
    if (!sceneId(ownerItemId) || !sceneId(sceneIdValue) || !sceneRecord(raw) || raw.version !== 1 || raw.sceneId !== sceneIdValue || raw.ownerItemId !== ownerItemId || !onlyKeys(raw, ['version', 'sceneId', 'ownerItemId', 'sourceReferencesEnabled', 'sourceReferences', 'capsule', 'regenFields', 'durationContext']))
        return fail('씬 설정의 소유권을 확인해 주세요.');
    if(raw.sourceReferencesEnabled!==undefined&&typeof raw.sourceReferencesEnabled!=='boolean')return fail('원본 제작 참조 사용 설정을 확인해 주세요.');
    const sourceReferences=raw.sourceReferences===undefined?undefined:readNativeProductionReferenceInventory(raw.sourceReferences);
    const capsule = helpers.preserveSceneGenSpec(raw.capsule);
    if (capsule.droppedKeys.length)
        return fail('보존할 수 없는 씬 설정이 있습니다. 설정을 다시 확인해 주세요.');
    let regenFields:SceneSpecRegenFields|undefined;
    if(raw.regenFields!==undefined){
        const fields=raw.regenFields;
        if(!sceneRecord(fields)||typeof fields.prompt!=='string'||fields.prompt.length>20000||Object.keys(fields).some(k=>!['prompt','extraRequest','durationSec','ambientSound','sfxNote','consistency'].includes(k)))return fail('제작 지시의 알려진 필드를 확인해 주세요.');
        for(const key of ['extraRequest','sfxNote'])if(fields[key]!==undefined&&(typeof fields[key]!=='string'||String(fields[key]).length>20000))return fail();
        if(fields.durationSec!==undefined&&!positive(fields.durationSec))return fail();
        if(fields.ambientSound!==undefined&&fields.ambientSound!==null&&(typeof fields.ambientSound!=='string'||fields.ambientSound.length>20000))return fail();
        if(fields.consistency!==undefined){
            const normalized=helpers.preserveSceneGenSpec({consistency:fields.consistency}).spec?.consistency;
            if(!normalized||editorValueSignature(normalized)!==editorValueSignature(fields.consistency))return fail('제작 지시의 일관성 설정을 다시 확인해 주세요.');
        }
        regenFields=copyEditorJson(fields) as unknown as SceneSpecRegenFields;
    }
    if (raw.durationContext !== undefined) {
        const c = raw.durationContext;
        if (!sceneRecord(c) || !onlyKeys(c, ['model', 'voiceSynced', 'audioSec', 'lastCueEndSec']) || (c.model !== undefined && c.model !== null && (typeof c.model !== 'string' || c.model.length > 128)) || (c.voiceSynced !== undefined && typeof c.voiceSynced !== 'boolean') || ['audioSec', 'lastCueEndSec'].some(k => c[k] !== undefined && c[k] !== null && (typeof c[k] !== 'number' || !Number.isFinite(c[k]) || Number(c[k]) < 0)))
            return fail();
    }
    return { version: 1, sceneId: sceneIdValue, ownerItemId, ...(typeof raw.sourceReferencesEnabled==='boolean'?{sourceReferencesEnabled:raw.sourceReferencesEnabled}:{}), ...(sourceReferences?{sourceReferences}:{}), ...(capsule.spec ? { capsule: capsule.spec } : {}), ...(regenFields ? {regenFields} : {}), ...(raw.durationContext ? { durationContext: copyEditorJson(raw.durationContext) as unknown as SceneSpecDurationInput } : {}) };
}
const sourceAnchorLineage = (item: Record<string, unknown>): string => sceneId(item.timelineSourceItemId) ? item.timelineSourceItemId : String(item.id);
const logicalSceneKey = (id: string, lineage: string): string => JSON.stringify([id, lineage]);
/** Locate a logical group by explicit anchor/ancestry, inherited lineage, or unique visible membership.
 * Overlapping provenance copies never become an arbitrary first-anchor choice. */
function selectedSourceSceneGroup(document: SceneGraph, itemId: string) {
    const item = document.items[itemId];
    if (!item || !sceneRecord(item.sourceScene) || !sceneId(item.sourceScene.id)) return fail();
    const id = item.sourceScene.id, ownership = sceneItemOwnership(document, itemId);
    const anchors = Object.values(document.items).filter(candidate => sceneRecord(candidate.sourceScene)
        && candidate.sourceScene.id === id && candidate.sourceScene.anchor === true);
    if (!anchors.length) return fail('씬 앵커를 다시 선택해 주세요.');
    const direct = anchors.includes(item) ? [item] : [];
    const ancestors = anchors.filter(anchor => ownership.ancestors.includes(anchor));
    const related = direct.length ? direct : ancestors;
    let lineages = new Set(related.map(sourceAnchorLineage));
    let selectedAnchors = related;
    if (!lineages.size) {
        const inherited = anchors.filter(anchor => sourceAnchorLineage(anchor) === sourceAnchorLineage(item));
        const overlapping = inherited.length ? [] : anchors.filter(anchor => {
            const window = sceneItemOwnership(document, String(anchor.id));
            return window.from < ownership.end && window.end > ownership.from;
        });
        selectedAnchors = inherited.length ? inherited : overlapping;
        lineages = new Set(selectedAnchors.map(sourceAnchorLineage));
        if (!lineages.size && new Set(anchors.map(sourceAnchorLineage)).size === 1)
            lineages = new Set(anchors.map(sourceAnchorLineage));
    }
    if (lineages.size !== 1) return fail('이 항목이 속한 씬을 하나로 확인하지 못했습니다. 씬 앵커를 다시 선택해 주세요.');
    const lineage = [...lineages][0]!;
    return { id, lineage, ownership, selectedAnchors: selectedAnchors.filter(anchor => sourceAnchorLineage(anchor) === lineage) };
}
/** Shared handoff/execution grouping: child IDs are not logical anchor lineages. */
export function sourceSceneGenerationGroupMembers(document: SceneGraph, itemId: string) {
    const selection = selectedSourceSceneGroup(document, itemId);
    const members = Object.values(document.items).filter(member => {
        if (!sceneRecord(member.sourceScene) || member.sourceScene.id !== selection.id) return false;
        try { return selectedSourceSceneGroup(document, String(member.id)).lineage === selection.lineage; }
        catch { return false; }
    });
    return { sceneId: selection.id, lineage: selection.lineage, members };
}
/** Generic clip cuts alias one declaration. Distinct copied lineages retain independent settings.
 * The optional lineage keeps the old single-group call compatible; multi-group callers must scope it. */
export function sourceSceneSpecOwnership(document: SceneGraph, sceneIdValue: string,
    helpers: Pick<SceneGenerationSemantics, 'preserveSceneGenSpec'> = canonicalPreserveHelpers, anchorLineage?: string) {
    const allAnchors = Object.values(document.items).filter(item => sceneRecord(item.sourceScene)
        && item.sourceScene.id === sceneIdValue && item.sourceScene.anchor === true);
    const lineages = new Set(allAnchors.map(sourceAnchorLineage));
    if (anchorLineage === undefined && lineages.size !== 1) return fail('씬 lineage를 하나로 지정해 주세요.');
    const lineage = anchorLineage ?? [...lineages][0]!;
    const anchors = allAnchors.filter(item => sourceAnchorLineage(item) === lineage);
    if (!anchors.length) return fail('씬 앵커를 다시 선택해 주세요.');
    const copies = Object.values(document.items).filter(item => sceneRecord(item.sourceScene)
        && item.sourceScene.id === sceneIdValue && sourceAnchorLineage(item) === lineage && item.sourceSceneSpec !== undefined);
    let stored: NativeSceneSpec | undefined;
    for (const item of copies) {
        if (!anchors.includes(item) || !sceneRecord(item.sourceSceneSpec) || !sceneId(item.sourceSceneSpec.ownerItemId))
            return fail('씬 설정의 소유권을 확인해 주세요.');
        const meta = readNativeSceneSpec(item.sourceSceneSpec, item.sourceSceneSpec.ownerItemId, sceneIdValue, helpers);
        if (editorValueSignature(meta) !== editorValueSignature(item.sourceSceneSpec)
            || (stored && editorValueSignature(stored) !== editorValueSignature(meta)))
            return fail('나뉜 씬 설정이 서로 다릅니다. 설정 소유권을 다시 확인해 주세요.');
        stored = meta;
    }
    const declaredOwner = stored ? document.items[stored.ownerItemId] : undefined;
    if (declaredOwner && !anchors.includes(declaredOwner)) return fail('다른 씬 lineage가 소유한 설정은 연결할 수 없습니다.');
    if (stored && !declaredOwner) {
        if (copies.some(item => !sceneId(item.timelineSourceItemId) || item.timelineSourceItemId !== lineage))
            return fail('이전 씬 설정의 분할 출처를 확인해 주세요.');
        const foreignClaim = Object.values(document.items).some(item => sceneRecord(item.sourceScene)
            && item.sourceScene.anchor === true
            && logicalSceneKey(String(item.sourceScene.id), sourceAnchorLineage(item)) !== logicalSceneKey(sceneIdValue, lineage)
            && sceneRecord(item.sourceSceneSpec) && item.sourceSceneSpec.ownerItemId === stored!.ownerItemId);
        if (foreignClaim) return fail('다른 씬 lineage의 이전 소유자는 이 설정으로 연결할 수 없습니다.');
    }
    return { anchors, copies, lineage, stored, declaredOwner };
}
/** Existing explicit owner survives selection/reorder. Otherwise the reviewed selected anchor
 * owns the settings; no timeline ordinal or arbitrary first-anchor election. */
export function resolveSourceSceneSpecOwner(document: SceneGraph, itemId: string,
    helpers: Pick<SceneGenerationSemantics, 'preserveSceneGenSpec'> = canonicalPreserveHelpers) {
    const selection = selectedSourceSceneGroup(document, itemId);
    const logical = sourceSceneSpecOwnership(document, selection.id, helpers, selection.lineage);
    const owner = logical.declaredOwner ?? (logical.anchors.length === 1 ? logical.anchors[0]
        : selection.selectedAnchors.length === 1 ? selection.selectedAnchors[0] : undefined);
    if (!owner) return fail('설정을 저장할 씬 조각 하나를 다시 선택해 주세요.');
    sceneItemOwnership(document, String(owner.id));
    const meta: NativeSceneSpec = logical.stored ? { ...logical.stored, ownerItemId: String(owner.id) }
        : { version: 1, ownerItemId: String(owner.id), sceneId: selection.id };
    return { ...logical, owner, meta };
}
async function captureSceneSpecTargetFromGraph(document: SceneGraph, scope: NativeWorkScope, revision: string, itemId: string): Promise<SceneSpecTarget> {
    const capturedScope = readSceneScope(scope);
    if (!sceneId(revision)) return fail();
    const selection = selectedSourceSceneGroup(document, itemId), { owner } = resolveSourceSceneSpecOwner(document, itemId);
    const item = document.items[itemId]!;
    const members = Object.values(document.items).filter(member => {
        if (!sceneRecord(member.sourceScene) || member.sourceScene.id !== selection.id) return false;
        try { return selectedSourceSceneGroup(document, String(member.id)).lineage === selection.lineage; }
        catch { return false; }
    });
    return { scope: capturedScope, revision, itemId, itemLineage: sourceAnchorLineage(item), sceneId: selection.id,
        ownerItemId: String(owner.id), documentSignature: await sourceSceneGenerationSignature(document),
        itemSignature: await sourceSceneGenerationSignature({ item, track: selection.ownership.track, ancestors: selection.ownership.ancestors }),
        sceneSignature: await sourceSceneGenerationSignature(members) };
}
export async function captureSceneSpecTarget(raw: unknown, scope: NativeWorkScope, revision: string, itemId: string): Promise<SceneSpecTarget> {
    return await captureSceneSpecTargetFromGraph(readSceneGraph(copyEditorJson(raw)), scope, revision, itemId);
}
export async function validateSceneSpecTarget(target: SceneSpecTarget, document: unknown, scope: NativeWorkScope, revision: string): Promise<void> {
    if (!target || !sceneScopeMatches(target.scope, scope) || target.revision !== revision)
        return fail('저장된 작업이 바뀌었습니다. 다시 불러오거나 선택해 주세요.');
    const now = await captureSceneSpecTarget(document, scope, revision, target.itemId);
    // A strict generation target is a compatible settings binding; sourceSignature is execution-only.
    const settings: SceneSpecTarget = { scope: target.scope, revision: target.revision, itemId: target.itemId,
        itemLineage: target.itemLineage, sceneId: target.sceneId, ownerItemId: target.ownerItemId,
        documentSignature: target.documentSignature, itemSignature: target.itemSignature, sceneSignature: target.sceneSignature };
    if (editorValueSignature(now) !== editorValueSignature(settings)) return fail();
}
export async function captureSceneGenerationTarget(raw: unknown, scope: NativeWorkScope, revision: string, itemId: string): Promise<SceneGenerationTarget> {
    const document = readSceneGraph(copyEditorJson(raw));
    const denied = () => fail('이 씬의 원본 설정 연결을 인계하지 못해 다시 만들기를 사용할 수 없습니다. 영상과 타임라인은 그대로 편집할 수 있습니다.');
    if (document.items[itemId]?.sourceSceneGenerationDisabled !== undefined) return denied();
    if (sourceSceneGenerationGroupMembers(document, itemId).members.some(item => item.sourceSceneGenerationDisabled !== undefined)) return denied();
    const settings = await captureSceneSpecTargetFromGraph(document, scope, revision, itemId);
    const source = await resolveSceneSourceWindow(document, itemId);
    return { ...settings, sourceSignature: await sourceSceneGenerationSignature(source) };
}
export async function validateSceneGenerationTarget(target: SceneGenerationTarget, document: unknown, scope: NativeWorkScope, revision: string): Promise<void> {
    if (!target || !sceneScopeMatches(target.scope, scope) || target.revision !== revision)
        return fail('저장된 작업이 바뀌었습니다. 다시 불러오거나 선택해 주세요.');
    const now = await captureSceneGenerationTarget(document, scope, revision, target.itemId);
    if (editorValueSignature(now) !== editorValueSignature(target)) return fail();
}
async function imageReference(document: SceneGraph, id: string, role: ResolvedSceneReference['role'], label?: string, castName?: string): Promise<ResolvedSceneReference> {
    const { asset, url } = trustedAsset(document, id, 'image');
    return { role, assetId: id, remoteUrl: url, signature: await sourceSceneGenerationSignature(asset), ...(label ? { label } : {}), ...(castName ? { castName } : {}) };
}
async function urlReference(document: SceneGraph, url: string, role: ResolvedSceneReference['role'], label?: string): Promise<ResolvedSceneReference> {
    const matches = Object.values(document.assets).filter(a => a.type === 'image' && a.remoteUrl === url);
    if (matches.length !== 1)
        return fail('설정에 연결된 이미지를 현재 작업의 자료함에서 다시 지정해 주세요.');
    return await imageReference(document, String(matches[0]!.id), role, label);
}
export async function readSourceSceneGenerationRequest(raw: unknown, capturedDocument: unknown, authority: {
    scope: NativeWorkScope;
    revision: string;
    retouchEnabled: boolean;
}, helpers: SceneGenerationSemantics): Promise<SourceSceneGenerationRequest> {
    if (!sceneRecord(raw) || raw.version !== 1 || (!sceneId(raw.requestId)||raw.requestId.length>128) || !['regenerate', 'retouch'].includes(String(raw.mode)) || raw.durationPolicy !== 'preserve-slot' || !sceneRecord(raw.target))
        return fail('영상 요청과 길이 정책을 확인해 주세요.');
    capturedDocument=copyEditorJson(capturedDocument);
    const target = copyEditorJson(raw.target) as unknown as SceneGenerationTarget;
    await validateSceneGenerationTarget(target, capturedDocument, authority.scope, authority.revision);
    const document = readSceneGraph(capturedDocument), source = await resolveSceneSourceWindow(document, target.itemId);
    const { owner, meta } = resolveSourceSceneSpecOwner(document, target.itemId, helpers);
    if (owner.id !== target.ownerItemId) return fail();
    const capsule = meta.capsule, slotSeconds = (source.end - source.from) / source.fps;
    const references: ResolvedSceneReference[] = [], mode = raw.mode as SourceSceneGenerationInput['mode'];
    let fields = meta.regenFields ?? { prompt: '' }, anchorMode: SceneSpecAnchorMode = 'keep', previousEnd: SourceWindow | undefined, instruction: VideoRetouchInstruction | undefined, retouchPrompt: string | undefined, estimate: VideoRetouchEstimate | undefined;
    if (mode === 'regenerate') {
        if (raw.instruction !== undefined)
            return fail('재생성과 영상 리터치를 따로 선택해 주세요.');
        const bounds = helpers.sceneSpecDurationBounds(meta.durationContext ?? {}), issue = helpers.sceneSpecDurationBlocked(capsule?.durationSec ?? slotSeconds, bounds);
        if (issue || bounds.cueExceedsCap || bounds.voiceExceedsCap)
            return fail(issue ?? '대사나 자막을 나누거나 길이를 조정한 뒤 다시 만들어 주세요.');
        // Slot never ripples implicitly; differing desired length requires a new reviewed timeline transaction.
        if (capsule?.durationSec !== undefined && Math.abs(capsule.durationSec - slotSeconds) > 1e-7)
            return fail('설정 길이와 씬 구간이 다릅니다. 씬 길이를 먼저 조정하거나 자동으로 바꿔 주세요.');
        fields = helpers.applySceneSpecToRegenFields(fields, capsule);
        fields = { ...fields, durationSec: slotSeconds };
        anchorMode = helpers.sceneSpecAnchorMode(capsule);
        if (capsule?.startAnchor?.kind === 'upload' && capsule.startAnchor.url)
            references.push(await urlReference(document, capsule.startAnchor.url, 'start'));
        if (capsule?.startAnchor?.kind === 'character-main' && capsule.startAnchor.castName) {
            const context = document.sourceSceneGeneration;
            const cast = context?.version === 1 && Array.isArray(context.cast) ? context.cast.filter(c => c.name === capsule.startAnchor!.castName) : [];
            if (cast.length !== 1 || !sceneId(cast[0]!.mainAssetId))
                return fail('등장인물의 기준 이미지를 다시 지정해 주세요.');
            references.push(await imageReference(document, cast[0]!.mainAssetId, 'start', undefined, cast[0]!.name));
        }
        if (anchorMode === 'prev-end') {
            const currentKey = logicalSceneKey(target.sceneId, sourceAnchorLineage(owner));
            const groups = new Map<string, { id: string; lineage: string; from: number; end: number }>();
            for (const anchor of Object.values(document.items)) {
                if (!sceneRecord(anchor.sourceScene) || anchor.sourceScene.anchor !== true || !sceneId(anchor.sourceScene.id)) continue;
                const lineage = sourceAnchorLineage(anchor), key = logicalSceneKey(anchor.sourceScene.id, lineage);
                if (key === currentKey) continue;
                const window = sceneItemOwnership(document, String(anchor.id)), prior = groups.get(key);
                groups.set(key, { id: anchor.sourceScene.id, lineage,
                    from: prior ? Math.min(prior.from, window.from) : window.from,
                    end: prior ? Math.max(prior.end, window.end) : window.end });
            }
            const prior = [...groups.values()].filter(group => group.end <= source.from).sort((a, b) => b.end - a.end);
            if (!prior.length || prior[0]!.end === prior[1]?.end)
                return fail('앞 씬을 확인하거나 시작 기준을 다시 지정해 주세요.');
            const chosen = prior[0]!;
            sourceSceneSpecOwnership(document, chosen.id, helpers, chosen.lineage);
            const candidates: Array<{ item: Record<string, unknown>; end: number }> = [];
            for (const item of Object.values(document.items)) {
                if (!sceneRecord(item.sourceScene) || item.sourceScene.id !== chosen.id || !['video', 'image'].includes(String(item.type))) continue;
                const window = sceneItemOwnership(document, String(item.id));
                if (window.from >= chosen.end || window.end <= chosen.from) continue;
                const selected = selectedSourceSceneGroup(document, String(item.id));
                if (selected.lineage === chosen.lineage) candidates.push({ item, end: window.end });
            }
            const ends = candidates.filter(candidate => candidate.end === chosen.end);
            if (ends.length !== 1) return fail('앞 씬 끝의 기준 컷 하나를 지정해 주세요.');
            previousEnd = await resolveSceneSourceWindow(document, String(ends[0]!.item.id));
        }
        if (capsule?.locationRef)
            references.push(await urlReference(document, capsule.locationRef.url, 'scene', capsule.locationRef.label));
        if (capsule?.aiRef)
            references.push(await urlReference(document, capsule.aiRef.url, 'reference', capsule.aiRef.label));
        if (fields.consistency?.anchorUrl)
            references.push(await urlReference(document, fields.consistency.anchorUrl, 'consistency'));
    }
    else {
        if (source.type !== 'video')
            return fail('완성된 영상 컷을 선택해 주세요.');
        if(document.assets[source.assetId]?.wakaSourceDurationUnknown===true)return fail('전체 원본 영상 길이를 확인하지 못했습니다. 전체 원본 파일을 자료함에 추가하거나 다시 만들기를 이용해 주세요.');
        // Current runner consumes whole physical file, not a trim. Never quote a window while billing full file.
        if (!isWholeNativeVideoSource(source))
            return fail('이 구간의 원본 전체 컷을 선택하거나 이 컷을 다시 만든 뒤 리터치해 주세요.');
        const eligible = helpers.videoRetouchEligibility({ id: target.itemId, videoUrl: source.remoteUrl, videoDurationSec: source.originalDurationSeconds }, { freeMode: authority.retouchEnabled });
        if (!eligible.ok)
            return fail(eligible.reason ?? '리터치를 사용할 수 없습니다.');
        const value = raw.instruction;
        if (!sceneRecord(value))
            return fail('고칠 내용을 선택해 주세요.');
        if (value.kind === 'preset' && value.presetId === 'fix-broken-hangul')
            instruction = { kind: 'preset', presetId: 'fix-broken-hangul' };
        else if (value.kind === 'free' && typeof value.text === 'string' && (value.target === undefined || typeof value.target === 'string')) {
            const issue = helpers.videoRetouchFreeTextIssue(value.text, value.target as string | undefined);
            if (issue)
                return fail(issue);
            instruction = { kind: 'free', text: value.text.trim(), ...(value.target ? { target: value.target as string } : {}) };
        }
        else
            return fail('고칠 내용을 다시 확인해 주세요.');
        retouchPrompt = helpers.omniRetouchPromptOf(instruction) ?? undefined;
        if (!retouchPrompt)
            return fail('고칠 내용을 적어 주세요.');
        estimate = helpers.omniRetouchEstimate(source.originalDurationSeconds);
    }
    const request = { version: 1 as const, requestId: raw.requestId, mode, target, durationPolicy: 'preserve-slot' as const, ...(instruction ? { instruction } : {}), source, ...(capsule ? { capsule: copyEditorJson(capsule) as unknown as SceneGenSpec } : {}), capsuleFingerprint: helpers.sceneGenSpecFingerprint(capsule), fields, anchorMode, references, ...(previousEnd ? { previousEnd } : {}), outputDurationSeconds: slotSeconds, ...(retouchPrompt ? { retouchPrompt } : {}), ...(estimate ? { estimate } : {}) };
    return { ...request, requestSignature: await sourceSceneGenerationSignature(request) };
}
export async function sourceGenerationRequestSignature(request: SourceSceneGenerationRequest): Promise<string> { const { requestSignature: _ignored, ...body } = request; return await sourceSceneGenerationSignature(body); }
export function hasSourceSceneGeneration(raw: unknown): boolean {
    return sceneRecord(raw) && (raw.sourceSceneGeneration !== undefined || (sceneRecord(raw.items) && Object.values(raw.items).some(i => sceneRecord(i) && (i.sourceSceneSpec !== undefined || i.sourceSceneGenerationDisabled !== undefined))) || (sceneRecord(raw.assets) && Object.values(raw.assets).some(a => sceneRecord(a) && a.wakaSceneGeneration !== undefined)));
}
type SceneSpecPreserveHelpers = Pick<SceneGenerationSemantics, 'preserveSceneGenSpec'>;
const canonicalPreserveHelpers: SceneSpecPreserveHelpers = { preserveSceneGenSpec };
function onlyKeys(raw: Record<string, unknown>, keys: readonly string[]): boolean {
    return Object.keys(raw).every(key => keys.includes(key));
}
const boundedText = (value: unknown, limit: number): value is string => typeof value === 'string' && value.length <= limit;
const digest = (value: unknown): value is string => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
const nonnegative = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0;
function httpsUrl(value: unknown): value is string {
    if (!boundedText(value, 8192)) return false;
    try { const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password; }
    catch { return false; }
}
function validCapsule(raw: unknown, helpers: SceneSpecPreserveHelpers): boolean {
    if (raw === undefined) return true;
    if (!sceneRecord(raw)) return false;
    const kept = helpers.preserveSceneGenSpec(raw);
    return !kept.droppedKeys.length && !!kept.spec && editorValueSignature(kept.spec) === editorValueSignature(raw);
}
function validTarget(raw: unknown): raw is SceneGenerationTarget {
    if (!sceneRecord(raw) || !onlyKeys(raw, ['scope', 'revision', 'itemId', 'itemLineage', 'sceneId', 'ownerItemId', 'documentSignature', 'itemSignature', 'sourceSignature', 'sceneSignature'])) return false;
    readSceneScope(raw.scope);
    return sceneRecord(raw.scope) && onlyKeys(raw.scope, ['projectId', 'workId', 'surface'])
        && ['revision', 'itemId', 'itemLineage', 'sceneId', 'ownerItemId'].every(key => sceneId(raw[key]))
        && ['documentSignature', 'itemSignature', 'sourceSignature', 'sceneSignature'].every(key => digest(raw[key]));
}
/** Only structurally verified receipt bindings may add conservative handoff denial.
 * Current asset consumers remain the authoritative binding when original item IDs disappeared.
 * These bindings never grant execution or replace async signature/scope/revision validation.
 */
export function sourceSceneGenerationReceiptTargetBindings(raw: unknown): SceneGenerationTarget[] {
    if (!sceneRecord(raw) || !sceneRecord(raw.request)) return [];
    const targets: SceneGenerationTarget[] = [];
    try { if (validTarget(raw.request.target)) targets.push(raw.request.target); } catch { /* malformed scope */ }
    try {
        if (targets.length && validTarget(raw.appliedTo) && sceneScopeMatches(raw.appliedTo.scope, targets[0]!.scope)
            && raw.appliedTo.itemLineage === targets[0]!.itemLineage
            && !(raw.appliedTo.sceneId !== targets[0]!.sceneId && raw.appliedTo.itemId === targets[0]!.itemId)) targets.push(raw.appliedTo);
    } catch { /* malformed applied scope */ }
    return targets;
}
function validSourceWindow(raw: unknown): raw is SourceWindow {
    if (!sceneRecord(raw) || !onlyKeys(raw, ['assetId', 'remoteUrl', 'assetSignature', 'from', 'end', 'fps', 'sourceStartSeconds', 'sourceEndSeconds', 'playbackRate', 'originalDurationSeconds', 'type'])
        || !sceneId(raw.assetId) || !httpsUrl(raw.remoteUrl) || !digest(raw.assetSignature)
        || !frame(raw.from) || !frame(raw.end) || raw.end <= raw.from || !positive(raw.fps)
        || !nonnegative(raw.sourceStartSeconds) || !positive(raw.sourceEndSeconds) || !positive(raw.playbackRate)
        || !positive(raw.originalDurationSeconds) || !['video', 'image'].includes(String(raw.type))) return false;
    const mappedEnd=raw.sourceStartSeconds+(raw.end-raw.from)/raw.fps*raw.playbackRate;
    const exact=Math.abs(raw.sourceEndSeconds-mappedEnd)<=1e-7;
    const quantized=raw.type==='video'&&raw.sourceEndSeconds===raw.originalDurationSeconds&&mappedEnd>raw.sourceEndSeconds&&mappedEnd-raw.sourceEndSeconds<raw.playbackRate/raw.fps-1e-7;
    return raw.sourceEndSeconds > raw.sourceStartSeconds && raw.sourceEndSeconds <= raw.originalDurationSeconds + 1e-7
        && (exact||quantized)
        && (raw.type !== 'image' || raw.playbackRate === 1);
}
function validRequest(raw: unknown, helpers: SceneSpecPreserveHelpers): raw is SourceSceneGenerationRequest {
    if (!sceneRecord(raw) || !onlyKeys(raw, ['version', 'requestId', 'mode', 'target', 'durationPolicy', 'instruction', 'source', 'capsule', 'capsuleFingerprint', 'fields', 'anchorMode', 'references', 'previousEnd', 'outputDurationSeconds', 'retouchPrompt', 'estimate', 'requestSignature'])
        || raw.version !== 1 || !sceneId(raw.requestId) || raw.requestId.length > 128 || !['regenerate', 'retouch'].includes(String(raw.mode))
        || raw.durationPolicy !== 'preserve-slot' || !validTarget(raw.target) || !validSourceWindow(raw.source)
        || !digest(raw.requestSignature) || !validCapsule(raw.capsule, helpers) || !boundedText(raw.capsuleFingerprint, 20000)
        || !['keep', 'none', 'prev-end', 'cast-main'].includes(String(raw.anchorMode)) || !positive(raw.outputDurationSeconds)
        || Math.abs(raw.outputDurationSeconds - (raw.source.end - raw.source.from) / raw.source.fps) > 1e-7
        || !Array.isArray(raw.references) || raw.references.length > 100) return false;
    // Reuse the actual native field validator, including canonical consistency preservation.
    readNativeSceneSpec({ version: 1, ownerItemId: raw.target.ownerItemId, sceneId: raw.target.sceneId, regenFields: raw.fields }, raw.target.ownerItemId, raw.target.sceneId, helpers);
    if (!sceneRecord(raw.fields)) return false;
    const refs = new Set<string>();
    for (const ref of raw.references) {
        if (!sceneRecord(ref) || !onlyKeys(ref, ['role', 'assetId', 'remoteUrl', 'signature', 'label', 'castName'])
            || !['start', 'scene', 'reference', 'consistency'].includes(String(ref.role)) || !sceneId(ref.assetId)
            || !httpsUrl(ref.remoteUrl) || !digest(ref.signature) || (ref.label !== undefined && !boundedText(ref.label, 1000))
            || (ref.castName !== undefined && (!boundedText(ref.castName, 80) || !ref.castName.trim() || ref.role !== 'start'))
            || refs.has(String(ref.role))) return false;
        refs.add(String(ref.role));
    }
    if (raw.previousEnd !== undefined && !validSourceWindow(raw.previousEnd)) return false;
    if (raw.mode === 'regenerate') {
        return raw.instruction === undefined && raw.retouchPrompt === undefined && raw.estimate === undefined
            && ((raw.anchorMode === 'prev-end') === (raw.previousEnd !== undefined))
            && (raw.anchorMode !== 'none' || !refs.has('start'))
            && (raw.anchorMode !== 'prev-end' || !refs.has('start'))
            && raw.fields.durationSec === raw.outputDurationSeconds;
    }
    const instruction = raw.instruction, estimate = raw.estimate;
    return isWholeNativeVideoSource(raw.source)
        && raw.anchorMode === 'keep' && !raw.references.length && raw.previousEnd === undefined
        && boundedText(raw.retouchPrompt, 17500) && !!raw.retouchPrompt.trim()
        && sceneRecord(instruction) && ((instruction.kind === 'preset' && instruction.presetId === 'fix-broken-hangul' && onlyKeys(instruction, ['kind', 'presetId']))
            || (instruction.kind === 'free' && onlyKeys(instruction, ['kind', 'text', 'target']) && boundedText(instruction.text, 16500) && !!instruction.text.trim()
                && (instruction.target === undefined || boundedText(instruction.target, 1000))))
        && sceneRecord(estimate) && onlyKeys(estimate, ['seconds', 'credits', 'notice']) && positive(estimate.seconds)
        && estimate.seconds === Math.ceil(raw.source.originalDurationSeconds)
        && nonnegative(estimate.credits) && boundedText(estimate.notice, 20000);
}
function validOriginalSnapshot(raw: unknown, id: string, targetSceneId: string, owner: boolean, helpers: SceneSpecPreserveHelpers): boolean {
    if (!sceneRecord(raw) || raw.id !== id || !boundedText(raw.type, 128) || !frame(raw.from)
        || !frame(raw.durationInFrames) || raw.durationInFrames === 0 || !sceneRecord(raw.sourceScene) || raw.sourceScene.id !== targetSceneId
        || (owner && raw.sourceScene.anchor !== true)) return false;
    if (!owner && (!['video', 'image'].includes(raw.type) || !sceneId(raw.assetId))) return false;
    if (raw.sourceSceneSpec !== undefined) {
        if (!sceneRecord(raw.sourceSceneSpec) || !sceneId(raw.sourceSceneSpec.ownerItemId)) return false;
        const recordedOwner = raw.sourceSceneSpec.ownerItemId;
        if (recordedOwner !== id && !sceneId(raw.timelineSourceItemId)) return false;
        const meta = readNativeSceneSpec(raw.sourceSceneSpec, recordedOwner, targetSceneId, helpers);
        if (editorValueSignature(meta) !== editorValueSignature(raw.sourceSceneSpec)) return false;
    }
    return true;
}
export function validCurrentSceneSpecReferences(meta: NativeSceneSpec, graph: SceneGraph): boolean {
    if(meta.sourceReferencesEnabled!==false&&meta.sourceReferences&&!nativeProductionReferencesAreCurrent(meta.sourceReferences,graph.assets))return false;
    const urls = [meta.capsule?.startAnchor?.kind === 'upload' ? meta.capsule.startAnchor.url : undefined,
        meta.capsule?.locationRef?.url, meta.capsule?.aiRef?.url, meta.capsule?.consistency?.anchorUrl, meta.regenFields?.consistency?.anchorUrl];
    if (urls.some(url => url !== undefined && (!httpsUrl(url) || Object.values(graph.assets).filter(asset => asset.type === 'image' && asset.remoteUrl === url).length !== 1))) return false;
    const castName = meta.capsule?.startAnchor?.kind === 'character-main' ? meta.capsule.startAnchor.castName : undefined;
    if (!castName) return true;
    const matches = graph.sourceSceneGeneration?.cast.filter(cast => cast.name === castName) ?? [];
    return matches.length === 1 && graph.assets[matches[0]!.mainAssetId]?.type === 'image'
        && httpsUrl(graph.assets[matches[0]!.mainAssetId]?.remoteUrl);
}
/** Pure synchronous load boundary for native-envelope/clipboard readers. This proves structural
 * safety only: receipt provenance may refer to deleted or foreign-work assets. Use the async
 * validator plus current target/scope/revision checks before any generation or reapplication.
 * Defaults to the real canonical helper; hosts may inject that same helper across alias boundaries.
 */
export type SourceSceneGenerationStructureReason =
    | 'invalid-document' | 'invalid-cast-context' | 'invalid-cast-entry' | 'duplicate-cast-name'
    | 'invalid-scene-anchor' | 'invalid-scene-spec' | 'missing-scene-spec'
    | 'stale-source-references' | 'invalid-reference-url' | 'missing-reference-image' | 'ambiguous-reference-image'
    | 'invalid-cast-reference' | 'invalid-output-receipt' | 'invalid-receipt-originals' | 'invalid-receipt-application';
export type SourceSceneGenerationStructureFailure = { path: string; reason: SourceSceneGenerationStructureReason };
export type SourceSceneGenerationStructureExplanation = { ok: boolean; failures: SourceSceneGenerationStructureFailure[] };
/** Paths use ordinals, never item IDs, cast names, URLs or exception messages. */
export function explainSourceSceneGenerationDocumentStructure(raw: unknown, helpers: SceneSpecPreserveHelpers = canonicalPreserveHelpers): SourceSceneGenerationStructureExplanation {
    const failures: SourceSceneGenerationStructureFailure[] = [];
    const reject = (path: string, reason: SourceSceneGenerationStructureReason) => { failures.push({ path, reason }); };
    let graph: SceneGraph;
    try { graph = readSceneGraph(copyEditorJson(raw)); }
    catch { return { ok: false, failures: [{ path: '$', reason: 'invalid-document' }] }; }
    const context = graph.sourceSceneGeneration;
    if (context !== undefined) {
        if (!sceneRecord(context) || !onlyKeys(context, ['version', 'cast']) || context.version !== 1 || !Array.isArray(context.cast) || context.cast.length > 100) {
            reject('sourceSceneGeneration', 'invalid-cast-context');
        } else {
            const names = new Set<string>();
            context.cast.forEach((c, index) => {
                const path = `sourceSceneGeneration.cast[${index}]`;
                if (!sceneRecord(c) || !onlyKeys(c, ['name', 'mainAssetId']) || !boundedText(c.name, 80) || !c.name.trim().length || !sceneId(c.mainAssetId) || graph.assets[c.mainAssetId]?.type !== 'image' || !httpsUrl(graph.assets[c.mainAssetId]?.remoteUrl)) reject(path, 'invalid-cast-entry');
                if (sceneRecord(c)) {
                    if (names.has(c.name)) reject(path, 'duplicate-cast-name');
                    names.add(c.name);
                }
            });
        }
    }
    const checkedScenes = new Set<string>();
    Object.values(graph.items).forEach((item, index) => {
        if (item.sourceSceneSpec === undefined) return;
        const path = `items[${index}].sourceSceneSpec`;
        if (!sceneRecord(item.sourceScene) || !sceneId(item.sourceScene.id) || item.sourceScene.anchor !== true) { reject(path, 'invalid-scene-anchor'); return; }
        const lineage = sourceAnchorLineage(item), key = logicalSceneKey(item.sourceScene.id, lineage);
        if (checkedScenes.has(key)) return;
        checkedScenes.add(key);
        try {
            const logical = sourceSceneSpecOwnership(graph, item.sourceScene.id, helpers, lineage), meta = logical.stored;
            if (!meta) { reject(path, 'missing-scene-spec'); return; }
            if (meta.sourceReferencesEnabled !== false && meta.sourceReferences && !nativeProductionReferencesAreCurrent(meta.sourceReferences, graph.assets)) reject(`${path}.sourceReferences`, 'stale-source-references');
            const refs = [
                ['capsule.startAnchor', meta.capsule?.startAnchor?.kind === 'upload' ? meta.capsule.startAnchor.url : undefined],
                ['capsule.locationRef', meta.capsule?.locationRef?.url], ['capsule.aiRef', meta.capsule?.aiRef?.url],
                ['capsule.consistency', meta.capsule?.consistency?.anchorUrl], ['regenFields.consistency', meta.regenFields?.consistency?.anchorUrl],
            ] as const;
            for (const [field, url] of refs) if (url !== undefined) {
                const count = Object.values(graph.assets).filter(asset => asset.type === 'image' && asset.remoteUrl === url).length;
                if (!httpsUrl(url)) reject(`${path}.${field}`, 'invalid-reference-url');
                else if (count !== 1) reject(`${path}.${field}`, count === 0 ? 'missing-reference-image' : 'ambiguous-reference-image');
            }
            const castName = meta.capsule?.startAnchor?.kind === 'character-main' ? meta.capsule.startAnchor.castName : undefined;
            if (castName) {
                const matches = Array.isArray(context?.cast) ? context.cast.filter(c => sceneRecord(c) && c.name === castName) : [];
                if (matches.length !== 1 || graph.assets[matches[0]!.mainAssetId]?.type !== 'image' || !httpsUrl(graph.assets[matches[0]!.mainAssetId]?.remoteUrl)) reject(`${path}.capsule.startAnchor`, 'invalid-cast-reference');
            }
        } catch { reject(path, 'invalid-scene-spec'); }
    });
    Object.values(graph.assets).forEach((asset, index) => {
        if (asset.wakaSceneGeneration === undefined) return;
        const path = `assets[${index}].wakaSceneGeneration`, receipt = asset.wakaSceneGeneration;
        try {
            if (asset.type !== 'video' || !positive(asset.durationInSeconds) || !httpsUrl(asset.remoteUrl) || !sceneRecord(receipt) || !onlyKeys(receipt, ['version', 'attachmentId', 'request', 'appliedTo', 'originalItem', 'originalOwner']) || receipt.version !== 1 || !sceneId(receipt.attachmentId) || receipt.attachmentId.length > 128 || !validRequest(receipt.request, helpers)) { reject(path, 'invalid-output-receipt'); return; }
            if (receipt.appliedTo === undefined) {
                if (receipt.originalItem !== undefined || receipt.originalOwner !== undefined) reject(path, 'invalid-receipt-originals');
            } else if (!validTarget(receipt.appliedTo) || !sceneScopeMatches(receipt.appliedTo.scope, receipt.request.target.scope)
                || receipt.appliedTo.itemLineage !== receipt.request.target.itemLineage
                || (receipt.appliedTo.sceneId !== receipt.request.target.sceneId && receipt.appliedTo.itemId === receipt.request.target.itemId)
                || !validOriginalSnapshot(receipt.originalItem, receipt.appliedTo.itemId, receipt.appliedTo.sceneId, false, helpers)
                || !validOriginalSnapshot(receipt.originalOwner, receipt.appliedTo.ownerItemId, receipt.appliedTo.sceneId, true, helpers)) reject(path, 'invalid-receipt-application');
        } catch { reject(path, 'invalid-output-receipt'); }
    });
    return { ok: failures.length === 0, failures };
}
export function validateSourceSceneGenerationDocumentStructure(raw: unknown, helpers: SceneSpecPreserveHelpers = canonicalPreserveHelpers): boolean {
    return explainSourceSceneGenerationDocumentStructure(raw, helpers).ok;
}
/** Existing async API retains signature verification; load validation alone is not execution authority. */
export async function validateSourceSceneGenerationDocument(raw: unknown, helpers: SceneSpecPreserveHelpers = canonicalPreserveHelpers): Promise<boolean> {
    try {
        const captured = copyEditorJson(raw);
        if (!validateSourceSceneGenerationDocumentStructure(captured, helpers)) return false;
        for (const asset of Object.values(readSceneGraph(captured).assets)) {
            const receipt = asset.wakaSceneGeneration as NativeSceneOutputReceipt | undefined;
            if (receipt && receipt.request.requestSignature !== await sourceGenerationRequestSignature(receipt.request)) return false;
        }
        return true;
    } catch { return false; }
}
